/**
 * CashFlowAlertConfig — Card per configurare la soglia alert cash flow.
 * Push automatica al lunedì mattina se le uscite settimanali superano la soglia.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, BellOff, Zap, Save } from "lucide-react";
import api from "@/api";

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function CashFlowAlertConfig() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["cashflow-alert-settings"],
    queryFn: () => api.get("/costi-variabili/rate-fornitori/cashflow-alert-settings").then((r) => r.data),
  });
  const [form, setForm] = React.useState(null);
  React.useEffect(() => {
    if (data && !form) setForm(data);
  }, [data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: (patch) => api.put("/costi-variabili/rate-fornitori/cashflow-alert-settings", patch).then((r) => r.data),
    onSuccess: (d) => {
      setForm(d);
      qc.invalidateQueries({ queryKey: ["cashflow-alert-settings"] });
      toast.success("Impostazioni salvate");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore salvataggio"),
  });

  const test = useMutation({
    mutationFn: () => api.post("/costi-variabili/rate-fornitori/cashflow-alert-test").then((r) => r.data),
    onSuccess: (r) => {
      if (r?.skipped) {
        toast.info(`Nessuna push inviata: ${r.skipped}. Uscite: ${eur(r?.summary?.totale_ivato || r?.totale_ivato || 0)}`);
      } else if (r?.delivered != null) {
        toast.success(
          r.over
            ? `Push inviate: ${r.delivered}/${r.admins} — soglia superata (${eur(r.totale_ivato)})`
            : `Report inviato: ${r.delivered}/${r.admins} — entro soglia (${eur(r.totale_ivato)})`
        );
      } else {
        toast.success("Test completato");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore test"),
  });

  if (isLoading || !form) return null;

  return (
    <div className="bg-white border border-orange-300 rounded-md p-5 space-y-3" data-testid="cashflow-alert-config">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-orange-100 flex items-center justify-center shrink-0">
          {form.enabled ? (
            <Bell className="w-5 h-5 text-orange-600" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-400" />
          )}
        </div>
        <div className="flex-1">
          <div className="overline text-orange-700">Alert cash flow · lunedì 08:15</div>
          <h3 className="font-display font-bold text-lg">Push automatica se le uscite superano la soglia</h3>
          <p className="text-slate-600 text-xs mt-1">
            Ogni lunedì mattina il sistema calcola le rate fornitori da pagare della settimana
            (lun-dom, con IVA). Se il totale supera la soglia impostata, tutti gli admin ricevono
            una push notification (fallback email) con dettaglio per fornitore.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500">Stato</label>
          <div className="mt-1 flex items-center gap-2">
            <button
              onClick={() => setForm({ ...form, enabled: !form.enabled })}
              data-testid="toggle-alert-enabled"
              className={`px-3 py-1.5 text-xs font-bold uppercase rounded-sm ${
                form.enabled
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-slate-200 text-slate-600 hover:bg-slate-300"
              }`}
            >
              {form.enabled ? "✓ Attivo" : "○ Disattivato"}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500">Soglia settimanale (€ IVA)</label>
          <input
            type="number"
            step="100"
            min="0"
            value={form.threshold_eur}
            onChange={(e) => setForm({ ...form, threshold_eur: Number(e.target.value) || 0 })}
            data-testid="input-threshold"
            data-no-uppercase
            className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-sm text-sm font-mono"
          />
          <div className="text-[10px] text-slate-500 mt-0.5">
            Soglia attuale: <b>{eur(form.threshold_eur)}</b>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500">Report positivo</label>
          <div className="mt-1">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.notify_when_below}
                onChange={(e) => setForm({ ...form, notify_when_below: e.target.checked })}
                data-testid="toggle-notify-below"
              />
              Invia anche se entro soglia (report ok)
            </label>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
        <button
          onClick={() => save.mutate(form)}
          disabled={save.isPending}
          data-testid="btn-save-alert"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-sm font-bold rounded-sm"
        >
          <Save className="w-4 h-4" />
          {save.isPending ? "Salvataggio…" : "Salva"}
        </button>
        <button
          onClick={() => test.mutate()}
          disabled={test.isPending}
          data-testid="btn-test-alert"
          className="inline-flex items-center gap-2 px-3 py-2 border border-orange-300 text-orange-700 hover:bg-orange-50 disabled:opacity-60 text-sm font-bold rounded-sm"
        >
          <Zap className="w-4 h-4" />
          {test.isPending ? "Invio…" : "Test push adesso"}
        </button>
        <div className="ml-auto text-[11px] text-slate-500">
          <b>Idempotency:</b> 1 sola push per admin ogni settimana (TTL 30gg).
        </div>
      </div>
    </div>
  );
}
